import logger from '../../utils/logger';
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginForm from './LoginForm';
import MfaVerification from './MfaVerification';
import { AuthContext } from '../../context/AuthContext';

/**
 * Login with MFA Wrapper Component
 *
 * Handles the two-step login process:
 * 1. Standard username/password authentication
 * 2. MFA verification if enabled for the user
 */
const LoginWithMfa = ({ onToggleForm }) => {
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' | 'mfa'
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  // Handle successful credential login
  const handleCredentialsSuccess = (loginResponse) => {
    // Check if MFA is required
    if (loginResponse.mfaRequired) {
      // Store login session data and move to MFA step
      setPendingLoginData(loginResponse);
      setLoginStep('mfa');
    } else {
      // Login complete, redirect happens in LoginForm
      // No action needed here
    }
  };

  // Handle successful MFA verification
  const handleMfaSuccess = (mfaResponse) => {
    // MFA verified successfully
    // Clear sensitive data
    setPendingLoginData(null);

    // Extract user data from response
    const userData = mfaResponse.data?.user || mfaResponse.data;

    if (userData) {
      // Update AuthContext to set authenticated state
      if (authContext && authContext.setUser) {
        authContext.setUser(userData);
      }

      // Also store in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(userData));

      // Use setTimeout to ensure state update completes before navigation
      setTimeout(() => {
        navigate('/home');
      }, 100);
    } else {
      logger.error('No user data in MFA response');
    }
  };

  // Handle MFA verification with recovery code
  const handleRecoveryCodeSuccess = (recoveryResponse) => {
    // Recovery code verified successfully
    // Clear sensitive data
    setPendingLoginData(null);

    // Extract user data from response
    const userData = recoveryResponse.data?.user || recoveryResponse.data;

    if (userData) {
      // Update AuthContext to set authenticated state
      if (authContext && authContext.setUser) {
        authContext.setUser(userData);
      }

      // Also store in localStorage for persistence
      localStorage.setItem('user', JSON.stringify(userData));

      // Redirect to home page
      navigate('/home');
    } else {
      logger.error('No user data in recovery response');
    }
  };

  // Handle cancel during MFA (go back to login)
  const handleMfaCancel = () => {
    setLoginStep('credentials');
    // Clear sensitive login data
    setPendingLoginData(null);
  };

  return (
    <>
      {loginStep === 'credentials' && (
        <LoginForm
          onLoginSuccess={handleCredentialsSuccess}
          onToggleForm={onToggleForm}
        />
      )}

      {loginStep === 'mfa' && (
        <MfaVerification
          onVerify={handleMfaSuccess}
          onUseRecoveryCode={handleRecoveryCodeSuccess}
          onCancel={handleMfaCancel}
          pendingLoginData={pendingLoginData}
        />
      )}
    </>
  );
};

export default LoginWithMfa;
